import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {Descriptor, MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/data-object/definition"
import {isPromise} from "@cm/utils"
import {ImageOpDataObject} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object"
import {HalImage} from "@cm/hal"
import {getMipLevelSize} from "@cm/hal/hal-image/utils"
import {Box2} from "@cm/math"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_VERY_HIGH} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof definition

export class ImageOpDataObjectHal extends ImageOpHal<Definition, ImageOpDataObjectHalData> {
    constructor(
        ctx: ImageOpContextHal,
        parameters: ImageOpHalParameters<Definition>,
        private outputDescriptor: Descriptor,
    ) {
        super(ctx)
        this.setParameters(parameters)
    }

    setParametersAndOutputDescriptor(parameters: ImageOpHalParameters<Definition>, outputDescriptor: Descriptor) {
        this.outputDescriptor = outputDescriptor
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpDataObjectHalData>): ImageOpDataObjectHalData {
        return new ImageOpDataObjectHalData({
            ...args,
            descriptor: this.outputDescriptor,
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_VERY_HIGH,
        })
    }
}

class ImageOpDataObjectHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpDataObjectHalData>) {
        super(args)
        this.imageOpDataObject = this.ctx.loadDataObjectImage({dataObjectId: this.parameters.dataObjectId, downloadFormat: this.parameters.downloadFormat})
    }

    protected async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const imageOpDataObject = isPromise(this.imageOpDataObject) ? await this.imageOpDataObject : this.imageOpDataObject
        if (!imageOpDataObject) {
            ImageOpDataObjectHalData.logger?.warn("No data object id set")
            return
        }
        // the stored data object may not match the requested descriptor (e.g. an upstream operator changed output size after this mask was authored)
        const nativeMipLevelSize = getMipLevelSize(imageOpDataObject.descriptor, mipLevelRegion.mipLevel)
        const validRegion = Box2.intersect(mipLevelRegion, {x: 0, y: 0, width: nativeMipLevelSize.width, height: nativeMipLevelSize.height})
        if (validRegion.isEmpty()) {
            outputImage.clear({region: mipLevelRegion, mipLevel: mipLevelRegion.mipLevel, color: {r: 0, g: 0, b: 0, a: 0}})
            return
        }
        if (validRegion.width !== mipLevelRegion.width || validRegion.height !== mipLevelRegion.height) {
            outputImage.clear({region: mipLevelRegion, mipLevel: mipLevelRegion.mipLevel, color: {r: 0, g: 0, b: 0, a: 0}})
        }
        await imageOpDataObject.pageSupplier.fetch({
            target: outputImage,
            mipLevelRegion: {...validRegion, mipLevel: mipLevelRegion.mipLevel},
            options,
        })
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, _sourceRegions: Regions) {
        throw new Error("Data object node has no source images")
    }

    private imageOpDataObject?: ImageOpDataObject | Promise<ImageOpDataObject>
}
